import { UTCDate } from '@date-fns/utc'
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
} from 'date-fns'

export function formatLatency(ms: number): string {
  if (ms >= 1000) {
    return (
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(ms / 1000) + 's'
    )
  }

  return (
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(ms) +
    'ms'
  )
}

export function formatMilliseconds(value: number) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(
    value
  )
}

export function formatDate(value: Date | string) {
  return format(new Date(`${value}`), 'LLL dd, y HH:mm')
}

/**
 * "Apr 29, 2026 at 9:57 PM" — the header stamp shared by the details sheets.
 *
 * Distinct from `formatDate`, which is the dense 24-hour form used in table
 * cells. This one is read once at the top of a sheet, so it spends the width on
 * being unambiguous.
 *
 * With `timeZone` the stamp is in that zone and names it ("9:57 PM PT"), for a
 * time read next to a schedule written in that zone. `shortGeneric` keeps the
 * label "PT" rather than flipping between PST and PDT.
 */
export function formatSheetTimestamp(iso: string, timeZone?: string): string {
  const d = new Date(iso)
  const datePart = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone,
  })
  const timePart = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
    timeZoneName: timeZone ? 'shortGeneric' : undefined,
  })
  return `${datePart} at ${timePart}`
}

/**
 * Compact "5m ago" label for dense lists, where date-fns' own
 * `formatDistanceToNow` ("about 5 minutes ago") is too wide to fit.
 *
 * Buckets truncate rather than round, matching `updatedAgoLabel` in
 * duties-metrics: rounding reads as the future ("2m ago" 91 seconds in).
 *
 * Distinct from `formatRelativeTime` in ConversationHistoryList, which spells the
 * same durations with spaces and no suffix ("5 min", "1 w").
 */
export function formatRelativeTime(
  value: Date | string,
  now: number = Date.now()
): string {
  const then = new Date(value)
  if (Number.isNaN(then.getTime())) return ''

  if (differenceInSeconds(now, then) < 60) return 'just now'

  const minutes = differenceInMinutes(now, then)
  if (minutes < 60) return `${minutes}m ago`

  const hours = differenceInHours(now, then)
  if (hours < 24) return `${hours}h ago`

  return `${differenceInDays(now, then)}d ago`
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

export function formatMinutes(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
    Math.round(value)
  )
}

export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** The UTC month of a date, e.g. "October 2026". Read in UTC so a browser
 *  west of UTC never names the month before. */
export function formatUtcMonth(value: string | Date): string {
  return format(new UTCDate(value), 'MMMM yyyy')
}

/** A per-minute rate in USD: at least two decimals, up to four. */
export function formatRate(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value)
}

/** A per-minute price with no currency symbol, for a column whose header
 *  already carries the unit: at least two decimals, up to four, as
 *  `formatRate` shows a rate. */
export function formatPrice(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value)
}

/** A money amount with no currency symbol, for a column whose header
 *  already carries the unit. */
export function formatAmount(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** A fraction as a percentage: 0.1 is "10%", 0.125 is "12.5%". */
export function formatPercent(fraction: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(fraction)
}

export function formatCompactNumber(value: number) {
  if (value >= 100 && value < 1000) {
    return value.toString() // Keep the number as is if it's in the hundreds
  } else if (value >= 1000 && value < 1000000) {
    return (value / 1000).toFixed(1) + 'k' // Convert to 'k' for thousands
  } else if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M' // Convert to 'M' for millions
  } else {
    return value.toString() // Optionally handle numbers less than 100 if needed
  }
}
